(() => {
  'use strict';

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  function getViewportHeight() {
    return (
      window.visualViewport?.height ||
      window.innerHeight ||
      document.documentElement.clientHeight
    );
  }

  function observeReveal(element, className = 'is-rev', rootMargin = '0px 0px -30% 0px') {
    if (!element) return null;

    if (!('IntersectionObserver' in window)) {
      element.classList.add(className);
      return null;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        element.classList.add(className);
        observer.disconnect();
      });
    }, {
      root: null,
      rootMargin,
      threshold: 0
    });

    observer.observe(element);
    return observer;
  }

  function preloadFeatureImages(section) {
    if (!section || section.__featureImagesObserved) return;
    section.__featureImagesObserved = true;

    const images = [...section.querySelectorAll('img[loading="lazy"]')];
    if (!images.length) return;

    const startLoading = () => {
      images.forEach((image) => {
        image.loading = 'eager';
        if (typeof image.decode === 'function') image.decode().catch(() => {});
      });
    };

    if (!('IntersectionObserver' in window)) {
      startLoading();
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      startLoading();
      observer.disconnect();
    }, {
      rootMargin: '1200px 0px',
      threshold: 0
    });

    observer.observe(section);
  }

  /* ============================================================
     ネイティブ position:sticky によるカードスタック（SP/PC共通）。
     位置固定そのものはブラウザに完全に任せ、JSは「次のカードが
     どれだけ被さってきたか」の演出計算(縮小・傾斜・暗転)のみを行う。
     スクロール位置のキャッシュや再計算のタイミング管理が一切不要になる
     （常にbrowserが最新の実レイアウトを反映してくれるため）。
  ============================================================ */

  function initStack(section) {
    if (!section || section.__stackInited) return;

    const intro = section.querySelector('[data-bwu-intro]');
    const cards = [...section.querySelectorAll('[data-bwu-card]')];

    if (!cards.length) return;

    section.__stackInited = true;

    const observers = [];
    const introObserver = observeReveal(intro, 'is-rev', '0px 0px -30% 0px');
    if (introObserver) observers.push(introObserver);

    cards.forEach((card) => {
      const observer = observeReveal(card, 'is-rev', '0px 0px -65% 0px');
      if (observer) observers.push(observer);
    });

    let pending = false;
    let destroyed = false;

    function render() {
      pending = false;
      if (destroyed) return;

      for (let index = 0; index < cards.length - 1; index++) {
        const card = cards[index];
        const cardHeight = card.offsetHeight;
        const pinTop = (getViewportHeight() - cardHeight) / 2;
        const nextTop = cards[index + 1].getBoundingClientRect().top;
        const progress = clamp((pinTop + cardHeight - nextTop) / cardHeight, 0, 1);

        if (progress <= 0) {
          if (card.style.transform) card.style.transform = '';
          if (card.style.filter) card.style.filter = '';
          continue;
        }

        card.style.transform =
          'perspective(1200px) translateY(' + (-28 * progress).toFixed(1) + 'px) rotateX(' +
          (9 * progress).toFixed(2) + 'deg) scale(' + (1 - 0.12 * progress).toFixed(4) + ')';
        card.style.filter = 'brightness(' + (1 - 0.45 * progress).toFixed(3) + ')';
      }
    }

    function requestRender() {
      if (pending || destroyed) return;
      pending = true;
      requestAnimationFrame(render);
    }

    window.addEventListener('scroll', requestRender, { passive: true });
    window.addEventListener('resize', requestRender, { passive: true });
    render();

    section.__stackDestroy = () => {
      destroyed = true;
      window.removeEventListener('scroll', requestRender);
      window.removeEventListener('resize', requestRender);
      observers.forEach((observer) => observer.disconnect());
      cards.forEach((card) => {
        card.style.transform = '';
        card.style.filter = '';
        card.classList.remove('is-rev');
      });
      section.__stackInited = false;
      section.__stackDestroy = null;
    };
  }

  function boot(root = document) {
    root.querySelectorAll('[data-bwithu-section]').forEach((section) => {
      preloadFeatureImages(section);
      initStack(section);
    });
  }

  document.addEventListener('DOMContentLoaded', () => boot());

  document.addEventListener('shopify:section:load', (event) => {
    boot(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-bwithu-section]').forEach((section) => {
      if (typeof section.__stackDestroy === 'function') section.__stackDestroy();
    });
  });

  if (document.readyState !== 'loading') {
    boot();
  }
})();